import { useState, useEffect, useRef } from "react";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  GripVertical,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Upload,
  ExternalLink,
  Image as ImageIcon,
  Plus,
  Trash2,
  Quote,
  Mail,
  Megaphone,
  Home,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

// Friendly labels and help text per block_type
const BLOCK_META = {
  hero_search: {
    label: "Hero + Search",
    description: "Top banner with background image, headline, subtitle, and search bar.",
    core: true,
  },
  featured_venues: {
    label: "Featured Venues",
    description: "Horizontal scroll of hand-picked venues (marked as featured).",
    core: true,
  },
  tonight_section: {
    label: "Tonight in Cyprus",
    description: "Today's events happening at your venues.",
    core: true,
  },
  browse_by_city: {
    label: "Browse by City",
    description: "Grid of city cards linking to filtered venue lists.",
    core: true,
  },
  browse_by_venue_type: {
    label: "Explore by Type",
    description: "Cards for restaurants / bars / clubs / cafes / tavernas.",
    core: true,
  },
  business_cta: {
    label: "Business CTA",
    description: "Final call-to-action banner for venue owners.",
    core: true,
  },
  testimonials: {
    label: "Testimonials",
    description: "Customer quote cards — build social proof with real feedback.",
    icon: Quote,
  },
  sponsored_banner: {
    label: "Sponsored Banner",
    description: "Wide clickable banner image for promotions or partnerships.",
    icon: Megaphone,
  },
  newsletter_signup: {
    label: "Newsletter Signup",
    description: "Email capture block with a subscribe button.",
    icon: Mail,
  },
};

// Types admins can add on top of the 6 core blocks
const ADDABLE_TYPES = ["testimonials", "sponsored_banner", "newsletter_signup"];

// Curated hero background library
const HERO_IMAGE_LIBRARY = [
  {
    label: "Cyprus sunset beach",
    url: "https://images.unsplash.com/photo-1675588372093-bc5c2502a78f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjeXBydXMlMjBiZWFjaCUyMGJhciUyMHN1bnNldHxlbnwwfHx8fDE3NzU4NTk3MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    label: "Seaside dining at dusk",
    url: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80",
  },
  {
    label: "Candlelit taverna table",
    url: "https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?auto=format&fit=crop&w=1600&q=80",
  },
  {
    label: "Mediterranean mezze",
    url: "https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=1600&q=80",
  },
  {
    label: "Rooftop cocktail bar",
    url: "https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=1600&q=80",
  },
  {
    label: "Wine & coastline",
    url: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=1600&q=80",
  },
  {
    label: "Morning coffee",
    url: "https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&w=1600&q=80",
  },
  {
    label: "Nightlife & neon",
    url: "https://images.unsplash.com/photo-1544148103-0773bf10d330?auto=format&fit=crop&w=1600&q=80",
  },
];

// Sensible starter payloads for admin-addable block types
const DEFAULT_CONTENT = {
  testimonials: {
    title: "What guests are saying",
    title_el: "Τι λένε οι επισκέπτες μας",
    subtitle: "Real reviews from real tables",
    subtitle_el: "Πραγματικές κριτικές από πραγματικούς πελάτες",
    content_json: {
      quotes: [
        {
          text: "Best mezze I've had in Cyprus. The view from the terrace is unreal.",
          text_el: "Το καλύτερο μεζέ που έχω φάει στην Κύπρο. Η θέα είναι απίστευτη.",
          author: "Elena K.",
          location: "Limassol",
        },
        {
          text: "Booked in 30 seconds, walked in, table was ready. This is how it should work.",
          text_el: "Κράτηση σε 30 δευτερόλεπτα, μπήκα, το τραπέζι ήταν έτοιμο.",
          author: "Marios P.",
          location: "Nicosia",
        },
      ],
    },
  },
  sponsored_banner: {
    title: "Summer promo",
    title_el: "Καλοκαιρινή προσφορά",
    subtitle: "",
    subtitle_el: "",
    content_json: {
      image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80",
      link: "/venues",
      alt_text: "Summer promo banner",
    },
  },
  newsletter_signup: {
    title: "Get the best of Cyprus in your inbox",
    title_el: "Τα καλύτερα της Κύπρου στο email σας",
    subtitle: "One weekly pick. No spam.",
    subtitle_el: "Μια επιλογή την εβδομάδα. Χωρίς spam.",
    content_json: {
      placeholder: "you@example.com",
      placeholder_el: "you@example.com",
      button_text: "Subscribe",
      button_text_el: "Εγγραφή",
      success_message: "Thanks — you're on the list!",
      success_message_el: "Ευχαριστούμε — είστε στη λίστα!",
    },
  },
};

export default function AdminHomepage() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/homepage/blocks`);
      setBlocks(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load blocks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlocks();
  }, []);

  const handleToggleVisible = async (block) => {
    const next = !block.is_visible;
    // optimistic
    setBlocks((prev) => prev.map((b) => (b.block_id === block.block_id ? { ...b, is_visible: next } : b)));
    try {
      await axios.put(`${API}/admin/homepage/blocks/${block.block_id}`, { is_visible: next });
      toast.success(next ? "Block is now visible" : "Block hidden");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
      fetchBlocks();
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(blocks);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setBlocks(reordered);
    try {
      await axios.post(`${API}/admin/homepage/blocks/reorder`, {
        ids: reordered.map((b) => b.block_id),
      });
      toast.success("Order saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save order");
      fetchBlocks();
    }
  };

  const handleSaveEdit = async (blockId, payload) => {
    try {
      const res = await axios.put(`${API}/admin/homepage/blocks/${blockId}`, payload);
      setBlocks((prev) => prev.map((b) => (b.block_id === blockId ? res.data : b)));
      setEditing(null);
      toast.success("Block updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };

  const handleAddBlock = async (blockType) => {
    setCreating(true);
    try {
      const defaults = DEFAULT_CONTENT[blockType] || {};
      const nextOrder = blocks.length
        ? Math.max(...blocks.map((b) => b.sort_order || 0)) + 1
        : 1;
      const payload = {
        block_type: blockType,
        title: defaults.title || "",
        title_el: defaults.title_el || "",
        subtitle: defaults.subtitle || "",
        subtitle_el: defaults.subtitle_el || "",
        content_mode: "manual",
        content_json: defaults.content_json || {},
        sort_order: nextOrder,
        is_visible: true,
      };
      const res = await axios.post(`${API}/admin/homepage/blocks`, payload);
      setBlocks((prev) => [...prev, res.data]);
      setShowAdd(false);
      setEditing(res.data);
      toast.success(`${BLOCK_META[blockType]?.label || blockType} added`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add section");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBlock = async (block) => {
    if (!window.confirm(`Delete the "${BLOCK_META[block.block_type]?.label || block.block_type}" section? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/homepage/blocks/${block.block_id}`);
      setBlocks((prev) => prev.filter((b) => b.block_id !== block.block_id));
      toast.success("Section deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div data-testid="admin-homepage-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><Home className="h-6 w-6 text-primary" />Homepage Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Edit, reorder, and show/hide the sections that appear on the public homepage.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowAdd(true)} data-testid="hp-add-section-btn">
            <Plus className="h-4 w-4 mr-2" /> Add section
          </Button>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            data-testid="homepage-preview-link"
          >
            <ExternalLink className="h-4 w-4" /> Preview live homepage
          </a>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        Drag the <GripVertical className="h-3 w-3 inline -mt-0.5" /> handle to reorder sections. Toggle the eye icon to
        show/hide a section without deleting it.
      </p>

      {loading ? (
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse bg-muted h-24 rounded-xl" />
          ))}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="homepage-blocks">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                {blocks.map((block, index) => {
                  const meta = BLOCK_META[block.block_type] || {
                    label: block.block_type,
                    description: "",
                  };
                  return (
                    <Draggable key={block.block_id} draggableId={block.block_id} index={index}>
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={`bg-card rounded-xl border border-border p-4 flex items-start gap-3 ${
                            snapshot.isDragging ? "shadow-lg ring-2 ring-primary/30" : ""
                          } ${!block.is_visible ? "opacity-60" : ""}`}
                          style={dragProvided.draggableProps.style}
                          data-testid={`hp-block-row-${block.block_id}`}
                        >
                          <div
                            {...dragProvided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground pt-1"
                            data-testid={`hp-block-drag-${block.block_id}`}
                          >
                            <GripVertical className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium uppercase tracking-wide px-2 py-0.5 bg-muted rounded">
                                {meta.label}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {block.block_type}
                              </span>
                            </div>
                            <p className="font-medium text-foreground truncate">
                              {block.title || <span className="italic text-muted-foreground">No title</span>}
                            </p>
                            {block.title_el && (
                              <p className="text-sm text-muted-foreground truncate">EL: {block.title_el}</p>
                            )}
                            {block.subtitle && (
                              <p className="text-xs text-muted-foreground mt-1 truncate">{block.subtitle}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">{meta.description}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleVisible(block)}
                              title={block.is_visible ? "Hide section" : "Show section"}
                              data-testid={`hp-block-toggle-${block.block_id}`}
                            >
                              {block.is_visible ? (
                                <Eye className="h-4 w-4 text-green-600" />
                              ) : (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditing(block)}
                              data-testid={`hp-block-edit-${block.block_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {!meta.core && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteBlock(block)}
                                data-testid={`hp-block-delete-${block.block_id}`}
                                title="Delete section"
                              >
                                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {editing && (
        <EditBlockDialog
          block={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => handleSaveEdit(editing.block_id, payload)}
        />
      )}

      <AddSectionDialog
        open={showAdd}
        creating={creating}
        onClose={() => setShowAdd(false)}
        onAdd={handleAddBlock}
      />
    </div>
  );
}

function AddSectionDialog({ open, creating, onClose, onAdd }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="hp-add-dialog">
        <DialogHeader>
          <DialogTitle>Add a new section</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
          {ADDABLE_TYPES.map((type) => {
            const meta = BLOCK_META[type];
            const Icon = meta.icon || Plus;
            return (
              <button
                key={type}
                type="button"
                onClick={() => onAdd(type)}
                disabled={creating}
                className="group text-left p-4 bg-card border border-border rounded-xl hover:border-primary hover:shadow-md transition-all disabled:opacity-50"
                data-testid={`hp-add-type-${type}`}
              >
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <p className="font-medium text-foreground">{meta.label}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {meta.description}
                </p>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          Sections are added at the bottom of the list. Drag to reorder after creation.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function EditBlockDialog({ block, open, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    title: block.title || "",
    title_el: block.title_el || "",
    subtitle: block.subtitle || "",
    subtitle_el: block.subtitle_el || "",
    is_visible: block.is_visible !== false,
    content_json: { ...(block.content_json || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const setContent = (key, value) => {
    setForm((prev) => ({ ...prev, content_json: { ...prev.content_json, [key]: value } }));
  };

  const handleUploadImage = async (e, field = "background_image") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Use current origin so uploaded image URLs work on the host the user is on
      const backend = (typeof window !== "undefined" && window.location?.origin) || "";
      const absolute = res.data.url.startsWith("http") ? res.data.url : `${backend}${res.data.url}`;
      setContent(field, absolute);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      title: form.title,
      title_el: form.title_el,
      subtitle: form.subtitle,
      subtitle_el: form.subtitle_el,
      is_visible: form.is_visible,
      content_json: form.content_json,
    };
    await onSave(payload);
    setSaving(false);
  };

  const blockType = block.block_type;
  const meta = BLOCK_META[blockType] || { label: blockType, description: "" };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hp-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit: {meta.label}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
            <div>
              <p className="text-sm font-medium">Visible on homepage</p>
              <p className="text-xs text-muted-foreground">Hide to remove without deleting.</p>
            </div>
            <Switch
              checked={form.is_visible}
              onCheckedChange={(v) => setForm({ ...form, is_visible: v })}
              data-testid="hp-edit-visible"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Title (EN)</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1"
                data-testid="hp-edit-title-en"
              />
            </div>
            <div>
              <Label>Title (EL)</Label>
              <Input
                value={form.title_el}
                onChange={(e) => setForm({ ...form, title_el: e.target.value })}
                className="mt-1"
                placeholder="Ελληνικός τίτλος"
                data-testid="hp-edit-title-el"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Subtitle (EN)</Label>
              <Textarea
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                className="mt-1"
                rows={2}
                data-testid="hp-edit-subtitle-en"
              />
            </div>
            <div>
              <Label>Subtitle (EL)</Label>
              <Textarea
                value={form.subtitle_el}
                onChange={(e) => setForm({ ...form, subtitle_el: e.target.value })}
                className="mt-1"
                rows={2}
                placeholder="Ελληνικός υπότιτλος"
                data-testid="hp-edit-subtitle-el"
              />
            </div>
          </div>

          {/* Block-specific fields */}
          {blockType === "hero_search" && (
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-sm font-medium flex items-center gap-2">
                <ImageIcon className="h-4 w-4" /> Hero background image
              </p>
              <Input
                value={form.content_json.background_image || ""}
                onChange={(e) => setContent("background_image", e.target.value)}
                placeholder="https://…"
                data-testid="hp-edit-hero-image"
              />
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleUploadImage}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="hp-edit-hero-upload"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {uploading ? "Uploading…" : "Upload new image"}
                </Button>
                {form.content_json.background_image && (
                  <img
                    src={form.content_json.background_image}
                    alt="preview"
                    className="h-16 w-28 object-cover rounded-md border border-border"
                  />
                )}
              </div>

              <div className="pt-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                  Or pick from the library
                </p>
                <div className="grid grid-cols-4 gap-2" data-testid="hp-hero-library">
                  {HERO_IMAGE_LIBRARY.map((img) => {
                    const selected = form.content_json.background_image === img.url;
                    return (
                      <button
                        key={img.url}
                        type="button"
                        onClick={() => setContent("background_image", img.url)}
                        className={`relative aspect-[16/10] overflow-hidden rounded-md border-2 transition-all ${
                          selected
                            ? "border-primary ring-2 ring-primary/30"
                            : "border-transparent hover:border-primary/50"
                        }`}
                        data-testid={`hp-hero-library-${img.url.split("/").pop().slice(0, 10)}`}
                        title={img.label}
                      >
                        <img src={img.url} alt={img.label} loading="lazy" className="w-full h-full object-cover" />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
                          <span className="text-[10px] text-white leading-tight line-clamp-1">{img.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {blockType === "featured_venues" && (
            <div className="space-y-2 border-t border-border pt-4">
              <Label>Number of venues to show</Label>
              <Input
                type="number"
                min={1}
                max={24}
                value={form.content_json.limit ?? 6}
                onChange={(e) => setContent("limit", parseInt(e.target.value) || 6)}
                className="w-32"
                data-testid="hp-edit-featured-limit"
              />
              <p className="text-xs text-muted-foreground">
                Only venues marked as "Featured" in Admin → Venues are shown here.
              </p>
            </div>
          )}

          {blockType === "business_cta" && (
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-sm font-medium">Call-to-action button</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Button text (EN)</Label>
                  <Input
                    value={form.content_json.cta_text || ""}
                    onChange={(e) => setContent("cta_text", e.target.value)}
                    className="mt-1"
                    placeholder="List Your Venue"
                    data-testid="hp-edit-cta-text-en"
                  />
                </div>
                <div>
                  <Label>Button text (EL)</Label>
                  <Input
                    value={form.content_json.cta_text_el || ""}
                    onChange={(e) => setContent("cta_text_el", e.target.value)}
                    className="mt-1"
                    placeholder="Καταχωρίστε τον Χώρο σας"
                    data-testid="hp-edit-cta-text-el"
                  />
                </div>
              </div>
              <div>
                <Label>Button link URL</Label>
                <Input
                  value={form.content_json.cta_link || ""}
                  onChange={(e) => setContent("cta_link", e.target.value)}
                  className="mt-1"
                  placeholder="/page/for-venues"
                  data-testid="hp-edit-cta-link"
                />
              </div>
            </div>
          )}

          {blockType === "testimonials" && (
            <TestimonialsEditor quotes={form.content_json.quotes || []} onChange={(q) => setContent("quotes", q)} />
          )}

          {blockType === "sponsored_banner" && (
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-sm font-medium flex items-center gap-2">
                <ImageIcon className="h-4 w-4" /> Banner image
              </p>
              <Input
                value={form.content_json.image || ""}
                onChange={(e) => setContent("image", e.target.value)}
                placeholder="https://…"
                data-testid="hp-edit-banner-image"
              />
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => handleUploadImage(e, "image")}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="hp-edit-banner-upload"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {uploading ? "Uploading…" : "Upload banner"}
                </Button>
                {form.content_json.image && (
                  <img
                    src={form.content_json.image}
                    alt="preview"
                    className="h-16 w-28 object-cover rounded-md border border-border"
                  />
                )}
              </div>
              <div>
                <Label>Click-through link</Label>
                <Input
                  value={form.content_json.link || ""}
                  onChange={(e) => setContent("link", e.target.value)}
                  className="mt-1"
                  placeholder="/venues"
                  data-testid="hp-edit-banner-link"
                />
              </div>
              <div>
                <Label>Alt text (accessibility)</Label>
                <Input
                  value={form.content_json.alt_text || ""}
                  onChange={(e) => setContent("alt_text", e.target.value)}
                  className="mt-1"
                  placeholder="Describe the banner for screen readers"
                  data-testid="hp-edit-banner-alt"
                />
              </div>
            </div>
          )}

          {blockType === "newsletter_signup" && (
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-sm font-medium">Email form copy</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Button text (EN)</Label>
                  <Input
                    value={form.content_json.button_text || ""}
                    onChange={(e) => setContent("button_text", e.target.value)}
                    className="mt-1"
                    placeholder="Subscribe"
                    data-testid="hp-edit-newsletter-btn-en"
                  />
                </div>
                <div>
                  <Label>Button text (EL)</Label>
                  <Input
                    value={form.content_json.button_text_el || ""}
                    onChange={(e) => setContent("button_text_el", e.target.value)}
                    className="mt-1"
                    placeholder="Εγγραφή"
                    data-testid="hp-edit-newsletter-btn-el"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Success message (EN)</Label>
                  <Input
                    value={form.content_json.success_message || ""}
                    onChange={(e) => setContent("success_message", e.target.value)}
                    className="mt-1"
                    placeholder="Thanks — you're on the list!"
                    data-testid="hp-edit-newsletter-success-en"
                  />
                </div>
                <div>
                  <Label>Success message (EL)</Label>
                  <Input
                    value={form.content_json.success_message_el || ""}
                    onChange={(e) => setContent("success_message_el", e.target.value)}
                    className="mt-1"
                    placeholder="Ευχαριστούμε — είστε στη λίστα!"
                    data-testid="hp-edit-newsletter-success-el"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="hp-edit-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestimonialsEditor({ quotes, onChange }) {
  const updateQuote = (idx, field, value) => {
    const next = quotes.map((q, i) => (i === idx ? { ...q, [field]: value } : q));
    onChange(next);
  };
  const removeQuote = (idx) => {
    onChange(quotes.filter((_, i) => i !== idx));
  };
  const addQuote = () => {
    onChange([...quotes, { text: "", text_el: "", author: "", location: "" }]);
  };

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium flex items-center gap-2">
          <Quote className="h-4 w-4" /> Customer quotes
        </p>
        <Button type="button" variant="outline" size="sm" onClick={addQuote} data-testid="hp-testimonials-add">
          <Plus className="h-3 w-3 mr-1" /> Add quote
        </Button>
      </div>
      {quotes.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No quotes yet — click "Add quote" to create one.</p>
      )}
      {quotes.map((q, idx) => (
        <div key={idx} className="p-3 bg-muted/40 rounded-lg space-y-2" data-testid={`hp-testimonial-${idx}`}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">#{idx + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeQuote(idx)}
              data-testid={`hp-testimonial-remove-${idx}`}
            >
              <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Textarea
              value={q.text}
              onChange={(e) => updateQuote(idx, "text", e.target.value)}
              placeholder="Quote text (EN)"
              rows={2}
              className="text-sm"
            />
            <Textarea
              value={q.text_el}
              onChange={(e) => updateQuote(idx, "text_el", e.target.value)}
              placeholder="Quote text (EL)"
              rows={2}
              className="text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={q.author}
              onChange={(e) => updateQuote(idx, "author", e.target.value)}
              placeholder="Author name (e.g. Elena K.)"
              className="h-8 text-sm"
            />
            <Input
              value={q.location}
              onChange={(e) => updateQuote(idx, "location", e.target.value)}
              placeholder="Location (e.g. Limassol)"
              className="h-8 text-sm"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

